import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Chip
} from '@mui/material';
import { Email, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import VerificationService from '../../services/verificationService';
import type { VerificationStatus } from '../../services/verificationService';

interface EmailVerificationProps {
  participantId: string;
  email: string;
  studyCode: string;
  studyName: string;
  onVerificationComplete: (status: VerificationStatus) => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
  participantId,
  email,
  studyCode,
  studyName,
  onVerificationComplete
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);

  const maxAttempts = 3;
  const cooldownTime = 30; // seconds

  // Handle resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendCode = async () => {
    setSendingCode(true);
    setError(null);
    setSuccess(null);

    try {
      await VerificationService.sendEmailVerificationCode({
        participantId,
        email,
        studyCode
      });

      setCodeSent(true);
      setSuccess('Verification code sent to your email!');
      setResendCooldown(cooldownTime);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!VerificationService.validateVerificationCode(verificationCode)) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await VerificationService.verifyEmailCode({
        participantId,
        email,
        studyCode,
        verificationCode
      });

      if (response.success) {
        setSuccess('Email verification successful!');
        // Get updated verification status
        const statusResponse = await VerificationService.getVerificationStatus(
          participantId,
          email,
          studyCode
        );
        if (statusResponse.data) {
          onVerificationComplete(statusResponse.data);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
      setAttempts(prev => prev + 1);
      
      if (attempts + 1 >= maxAttempts) {
        setError('Too many failed attempts. Please request a new code.');
        setCodeSent(false);
        setVerificationCode('');
        setAttempts(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setAttempts(0);
    setError(null);
    setSuccess(null);
    setVerificationCode('');
    await handleSendCode();
  };

  const handleCodeChange = (value: string) => {
    // Only allow digits and limit to 6 characters
    const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(digitsOnly);
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Email sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Email Verification
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Verify your email address to continue with the study
          </Typography>
          <Chip 
            label={studyName} 
            color="primary" 
            variant="outlined" 
            sx={{ mb: 2 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>Participant ID:</strong> {participantId}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>Email:</strong> {email}
          </Typography>
        </Box>

        {!codeSent ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Click the button below to send a verification code to your email address.
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSendCode}
              disabled={sendingCode}
              startIcon={sendingCode ? <CircularProgress size={20} /> : <Email />}
            >
              {sendingCode ? 'Sending Code...' : 'Send Verification Code'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Enter the 6-digit verification code sent to your email:
            </Typography>

            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="123456"
              inputProps={{
                maxLength: 6,
                style: { 
                  textAlign: 'center', 
                  fontSize: '1.5rem',
                  letterSpacing: '0.5em'
                }
              }}
              sx={{ mb: 3 }}
              disabled={loading}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.length !== 6}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleResendCode}
                disabled={resendCooldown > 0 || sendingCode}
                size="small"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
              </Button>
            </Box>

            {attempts > 0 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 2, textAlign: 'center' }}>
                Attempts: {attempts}/{maxAttempts}
              </Typography>
            )}
          </Box>
        )}

        {error && (
          <Alert 
            severity="error" 
            sx={{ mt: 2 }}
            icon={<ErrorIcon />}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ mt: 2 }}
            icon={<CheckCircle />}
          >
            {success}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default EmailVerification;